import { Link } from "react-router";
import { useLookupCatalog } from "src/dm/hooks/useAdminLookups";

const friendlyName = (key: string) =>
  key
    .split("-")
    .map((p) => p[0].toUpperCase() + p.slice(1))
    .join(" ");

// Bespoke admin tiles (not driven by the generic /admin/lookups catalog).
// Most render under /admin/<key>; a couple (the operational utilities) live
// at top-level routes that pre-date the /admin grouping, so they carry an
// explicit `to` to override the default `/dm/admin/${key}` link.
const bespokeTiles: Array<{
  key: string;
  label: string;
  caption: string;
  to?: string;
}> = [
  {
    key: "deal-mappings",
    label: "Deal Mappings",
    caption: "DEAL_MAPPINGS · cat-mgr → owner / entry types / business unit",
  },
  {
    key: "deal-owner-changes",
    label: "Deal Owner Changes",
    caption: "Re-assign deal ownership for one deal or in bulk",
  },
  {
    key: "user-information",
    label: "Show User Information",
    caption: "Look up identity, application access, and CM-code access",
  },
  {
    key: "terms",
    label: "Terms & Conditions",
    caption: "TERMS_CONDITIONS · full body + active flag/date",
  },
  {
    key: "manage-item-cache",
    label: "Manage Item Cache",
    caption: "Invalidate the per-(cat-mgr × banner) item lookup cache",
    to: "/dm/manage-item-cache",
  },
  {
    key: "manage-load-failed-deals",
    label: "Manage Load-Failed Deals",
    caption: "Approve or Review deals stuck in LOADFAILED",
    to: "/dm/manage-load-failed-deals",
  },
];

const tileStyle: React.CSSProperties = {
  background: "#fff",
  padding: "1rem 1.25rem",
  borderRadius: "8px",
  boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
  textDecoration: "none",
  color: "#0f172a",
  display: "flex",
  flexDirection: "column",
  gap: "0.25rem",
};

const AdminLanding = () => {
  const { data, isLoading, isError, error } = useLookupCatalog();

  return (
    <div style={{ display: "grid", gap: "1.25rem" }}>
      <div>
        <h1 style={{ marginTop: 0, marginBottom: "0.25rem" }}>Admin</h1>
        <p style={{ color: "#475569", margin: 0 }}>
          Manage the lookup tables that drive deal-entry dropdowns, plus
          bespoke admin workflows.
        </p>
      </div>

      <section>
        <h2 style={{ fontSize: "1rem", marginBottom: "0.5rem" }}>
          Workflows
        </h2>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))",
            gap: "0.75rem",
          }}
        >
          {bespokeTiles.map((tile) => (
            <Link
              key={tile.key}
              to={tile.to ?? `/dm/admin/${tile.key}`}
              style={tileStyle}
            >
              <span style={{ fontWeight: 600 }}>{tile.label}</span>
              <span style={{ fontSize: "0.75rem", color: "#64748b" }}>
                {tile.caption}
              </span>
            </Link>
          ))}
        </div>
      </section>

      <section>
        <h2 style={{ fontSize: "1rem", marginBottom: "0.5rem" }}>
          Lookup tables
        </h2>
        {isLoading && <p>Loading…</p>}
        {isError && (
          <p style={{ color: "#b91c1c" }}>
            {error instanceof Error ? error.message : "Failed to load"}
          </p>
        )}

        {data && (
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))",
              gap: "0.75rem",
            }}
          >
            {data.map((def) => (
              <Link key={def.key} to={`/dm/admin/${def.key}`} style={tileStyle}>
                <span style={{ fontWeight: 600 }}>{friendlyName(def.key)}</span>
                <span style={{ fontSize: "0.75rem", color: "#64748b" }}>
                  {def.table} · key {def.keyColumn}
                </span>
              </Link>
            ))}
          </div>
        )}
      </section>
    </div>
  );
};

export default AdminLanding;
